/**
 * @fileoverview ProviderEntryForm — Single provider form card.
 *
 * Unified shape for both imported (pre-filled) and manual (empty) providers.
 * Provider name field auto-resolves against the directory (same pattern as
 * attorney/case manager). All fields are always visible and editable.
 *
 * On "Add Provider" it creates the provider via the directory API and calls onAdded.
 */
import {
  Building2,
  Check,
  Globe,
  Loader2,
  MapPin,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useProviderResolveSearch } from '@/hooks/useProviderResolveSearch';
import { toTitleCase } from '@/lib/strings';
import {
  enhancedApi as providerDirectoryV2GooglePlacesApi,
  type GooglePlacesAutocompletePredictionV2,
} from '@/services/api/generated/providerDirectoryV2GooglePlacesService';
import { useProviderDirectoryV2CreateServiceMutation } from '@/services/api/generated/providerDirectoryV2IdentityService';
import {
  medicalProvidersApiClient,
  useUpdateMedicalProviderMutation,
} from '@/services/api/treatmentService';
import {
  ProviderTreatmentStatus,
  TreatmentCareLevel,
} from '@/services/types/treatment-types';
import { compactLocation } from '@/utils/addressFormatting';
import type { ResolveProviderMatch } from '@/utils/providerDraft';
import { toProviderTypeHint } from '@/utils/providerDraft';

import {
  FormError,
  FormField,
  FormGroup,
  FormInput,
  FormLabel,
  FormSelect,
} from './form';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProviderFormData {
  name: string;
  type: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postcode: string;
  phone: string;
  fax: string;
  treatment_status: ProviderTreatmentStatus | '';
  care_level: TreatmentCareLevel | '';
  provider_type: 'hospital' | 'clinic' | '';
}

export const EMPTY_PROVIDER: ProviderFormData = {
  name: '',
  type: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postcode: '',
  phone: '',
  fax: '',
  treatment_status: '',
  care_level: '',
  provider_type: '',
};

interface ProviderEntryFormProps {
  /** Initial form data (pre-filled from import or empty) */
  data: ProviderFormData;
  /** Whether fields were pre-filled from import (shows AI badges) */
  isImported: boolean;
  /** Whether this is editing an existing provider (changes button text) */
  isEditing?: boolean;
  /** Index in the list (for display) */
  index: number;
  matterId: string;
  onAdded: () => void;
  onRemove: () => void;
}

function getProviderFormError(form: ProviderFormData): string | null {
  if (!form.name.trim()) {
    return 'Provider name is required';
  }

  if (!form.provider_type) {
    return 'Select a provider type';
  }

  if (!form.treatment_status) {
    return 'Select treatment status';
  }

  if (!form.care_level) {
    return 'Select treatment type';
  }

  return null;
}

function getDirectoryStatusContent(options: {
  name: string;
  isSearching: boolean;
  hasSearched: boolean;
  bestMatch: ResolveProviderMatch | null;
  onSearchDirectory: () => void;
}): React.JSX.Element | null {
  const {
    name,
    isSearching,
    hasSearched,
    bestMatch,
    onSearchDirectory,
  } = options;

  if (name.length < 3) {
    return null;
  }

  if (isSearching) {
    return (
      <p className="flex items-center gap-1 text-[11px] text-zinc-400">
        <Loader2 className="h-3 w-3 animate-spin" />
        Searching directory…
      </p>
    );
  }

  if (bestMatch) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-green-600">
        <Check className="h-3 w-3" />
        <span>
          Matched: <span className="font-medium">{bestMatch.name}</span>
          {bestMatch.formatted_address && (
            <span className="ml-1 text-zinc-400">· {bestMatch.formatted_address}</span>
          )}
        </span>
      </div>
    );
  }

  if (!hasSearched) {
    return null;
  }

  return (
    <p className="text-[11px] text-zinc-400">
      No directory match — will create new provider ·{' '}
      <button
        type="button"
        onClick={onSearchDirectory}
        className="font-medium text-blue-500 hover:text-blue-700 hover:underline"
      >
        Search directory
      </button>
    </p>
  );
}

function getAddButtonLabel(isEditing: boolean): string {
  return isEditing ? 'Save Changes' : 'Add Provider';
}

// ── Component ────────────────────────────────────────────────────────────────

export const ProviderEntryForm = ({
  data: initialData,
  isImported,
  isEditing = false,
  index,
  matterId,
  onAdded,
  onRemove,
}: ProviderEntryFormProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const [form, setForm] = useState<ProviderFormData>(initialData);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const search = useProviderResolveSearch({ matterId });
  const modalSearch = useProviderResolveSearch({ matterId });
  const hasSearched = useRef(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [createMatterProvider] = useProviderDirectoryV2CreateServiceMutation();
  const [updateMedicalProvider] = useUpdateMedicalProviderMutation();
  const [googleDetails, { isFetching: isFetchingGoogleDetails }] =
    providerDirectoryV2GooglePlacesApi.endpoints.providerDirectoryV2GooglePlacesDetails.useLazyQuery();

  const bestMatch = useMemo(() => search.resolveMatches[0] ?? null, [search.resolveMatches]);
  const nameMatchesImport = isImported && form.name === initialData.name;

  const formattedAddress = useMemo(() => {
    const parts: string[] = [];

    if (form.address_line1) {
      parts.push(form.address_line1);
    }

    if (form.address_line2) {
      parts.push(form.address_line2);
    }

    const cityState = [form.city, form.state].filter(Boolean).join(', ');
    if (cityState) {
      parts.push(cityState);
    }

    if (form.postcode) {
      parts.push(form.postcode);
    }

    return parts.join(', ');
  }, [form]);

  const canAdd = Boolean(
    form.name.trim() &&
      form.provider_type &&
      form.treatment_status &&
      form.care_level &&
      !isAdding,
  );

  function update<K extends keyof ProviderFormData>(
    field: K,
    value: ProviderFormData[K],
  ): void {
    setForm((currentForm) => ({ ...currentForm, [field]: value }));
    setError(null);
  }

  function handleNameChange(value: string): void {
    update('name', value);
    hasSearched.current = false;
    clearTimeout(searchTimeoutRef.current);

    if (value.length < 3) {
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      search.setQuery(value);
      hasSearched.current = true;
    }, 500);
  }

  useEffect(() => {
    if (hasSearched.current || !form.name || form.name.length < 3) {
      return;
    }

    hasSearched.current = true;
    search.setQuery(form.name);
  }, [form.name, search]);

  useEffect(() => {
    return () => {
      clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleAdd = useCallback(async (): Promise<void> => {
    const validationError = getProviderFormError(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      const result = await createMatterProvider({
        providerDirectoryCreateServiceRequestV2: {
          matter_id: matterId,
          source: bestMatch ? 'internal_existing' : 'manual',
          base_provider_service_id: bestMatch?.provider_service_id ?? null,
          service_name: form.name.trim(),
          service_type: 'GENERAL',
          provider_type_hint: toProviderTypeHint(form.provider_type as 'hospital' | 'clinic'),
          address: {
            formatted_address: formattedAddress || null,
            address_line1: form.address_line1.trim() || null,
            address_line2: form.address_line2.trim() || null,
            city: form.city.trim() || null,
            state: form.state.trim() || null,
            postal_code: form.postcode.trim() || null,
            country: 'US',
            latitude: null,
            longitude: null,
            google_place_id: null,
          },
          phone: form.phone.trim() || null,
          fax: form.fax.trim() || null,
          website: null,
        },
      }).unwrap();

      dispatch(
        medicalProvidersApiClient.util.invalidateTags([
          { type: 'MedicalProviders', id: matterId },
        ]),
      );

      if (result.medical_provider_id) {
        try {
          await updateMedicalProvider({
            matterId,
            providerId: result.medical_provider_id,
            provider: {
              treatment_status: form.treatment_status as ProviderTreatmentStatus,
              care_level: form.care_level as TreatmentCareLevel,
            },
          }).unwrap();
        } catch {
          // non-blocking
        }
      }

      toast.success(`Added ${form.name.trim()}`);
      onAdded();
    } catch {
      toast.error(`Failed to add ${form.name.trim()}`);
    } finally {
      setIsAdding(false);
    }
  }, [
    bestMatch,
    createMatterProvider,
    dispatch,
    form,
    formattedAddress,
    matterId,
    onAdded,
    updateMedicalProvider,
  ]);

  const handlePickInternalFromModal = useCallback(
    (match: ResolveProviderMatch): void => {
      setForm((currentForm) => ({
        ...currentForm,
        name: match.name,
        address_line1: match.address_line1 ?? currentForm.address_line1,
        address_line2: match.address_line2 ?? currentForm.address_line2,
        city: match.city ?? currentForm.city,
        state: match.state ?? currentForm.state,
        postcode: match.postal_code ?? currentForm.postcode,
        phone: match.phone ?? currentForm.phone,
      }));
      search.setQuery(match.name);
      hasSearched.current = true;
      setShowSearchModal(false);
    },
    [search],
  );

  const handlePickGoogleFromModal = useCallback(
    async (prediction: GooglePlacesAutocompletePredictionV2): Promise<void> => {
      try {
        const details = await googleDetails({
          placeId: prediction.place_id,
          sessionToken: modalSearch.googleSessionToken,
        }).unwrap();
        modalSearch.rotateGoogleSessionToken();

        setForm((currentForm) => ({
          ...currentForm,
          name: details.name ?? prediction.main_text ?? currentForm.name,
          address_line1: details.address_line1 ?? currentForm.address_line1,
          address_line2: details.address_line2 ?? currentForm.address_line2,
          city: details.city ?? currentForm.city,
          state: details.state ?? currentForm.state,
          postcode: details.postal_code ?? currentForm.postcode,
          phone: details.phone_number ?? currentForm.phone,
        }));

        search.setQuery(details.name ?? prediction.main_text ?? form.name);
        hasSearched.current = true;
        setShowSearchModal(false);
      } catch {
        toast.error('Unable to load Google place details');
      }
    },
    [form.name, googleDetails, modalSearch, search],
  );

  const openSearchModal = useCallback((): void => {
    modalSearch.setQuery(form.name);
    setShowSearchModal(true);
  }, [form.name, modalSearch]);

  const directoryStatusContent = getDirectoryStatusContent({
    name: form.name,
    isSearching: search.isSearching,
    hasSearched: search.hasSearched,
    bestMatch,
    onSearchDirectory: openSearchModal,
  });

  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
      <div className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50 px-4 py-2.5">
        <span className="text-xs font-semibold text-zinc-600">
          {isImported && <Sparkles className="-mt-0.5 mr-1 inline h-3 w-3 text-blue-500" />}
          {isEditing ? 'Edit Provider' : `Provider ${index + 1}`}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="space-y-4 px-4 py-3">
        <FormGroup>
          <FormField assisted={isImported && nameMatchesImport}>
            <FormLabel required>Provider Name</FormLabel>
            <FormInput
              value={form.name}
              onChange={(event) => handleNameChange(event.target.value)}
              placeholder="e.g., Kaiser Permanente - Orthopedics"
              disabled={isAdding}
              aria-label="Provider Name"
            />
            {directoryStatusContent && <div className="mt-1">{directoryStatusContent}</div>}
          </FormField>

          <div className="space-y-2">
            <FormField
              assisted={
                isImported &&
                Boolean(initialData.address_line1) &&
                form.address_line1 === initialData.address_line1
              }
            >
              <FormLabel>Address</FormLabel>
              <FormInput
                value={form.address_line1}
                onChange={(event) => update('address_line1', event.target.value)}
                placeholder="Street address"
                disabled={isAdding}
                aria-label="Address"
              />
            </FormField>
            <FormInput
              value={form.address_line2}
              onChange={(event) => update('address_line2', event.target.value)}
              placeholder="Apt / Suite (optional)"
              disabled={isAdding}
              aria-label="Address Line 2"
            />
            <div className="grid grid-cols-3 gap-2">
              <FormInput
                value={form.city}
                onChange={(event) => update('city', event.target.value)}
                placeholder="City"
                disabled={isAdding}
                aria-label="City"
              />
              <FormInput
                value={form.state}
                onChange={(event) => update('state', event.target.value)}
                placeholder="State"
                disabled={isAdding}
                aria-label="State"
              />
              <FormInput
                value={form.postcode}
                onChange={(event) => update('postcode', event.target.value)}
                placeholder="ZIP"
                disabled={isAdding}
                aria-label="ZIP"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              assisted={isImported && Boolean(initialData.phone) && form.phone === initialData.phone}
            >
              <FormLabel>Phone</FormLabel>
              <FormInput
                value={form.phone}
                onChange={(event) => update('phone', event.target.value)}
                placeholder="(555) 123-4567"
                disabled={isAdding}
                aria-label="Phone"
              />
            </FormField>
            <FormField assisted={isImported && Boolean(initialData.fax) && form.fax === initialData.fax}>
              <FormLabel>Fax</FormLabel>
              <FormInput
                value={form.fax}
                onChange={(event) => update('fax', event.target.value)}
                placeholder="(555) 123-4567"
                disabled={isAdding}
                aria-label="Fax"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FormField>
              <FormLabel required>Status</FormLabel>
              <FormSelect
                value={form.treatment_status}
                onChange={(event) => {
                  update(
                    'treatment_status',
                    event.target.value as ProviderTreatmentStatus | '',
                  );
                }}
                placeholder="Select"
                disabled={isAdding}
                aria-label="Status"
              >
                {Object.values(ProviderTreatmentStatus)
                  .filter((status) => status !== ProviderTreatmentStatus.NEVER_TREATED)
                  .map((status) => (
                    <option key={status} value={status}>
                      {toTitleCase(status.replace(/_/g, ' '))}
                    </option>
                  ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel required>Care Level</FormLabel>
              <FormSelect
                value={form.care_level}
                onChange={(event) => {
                  update('care_level', event.target.value as TreatmentCareLevel | '');
                }}
                placeholder="Select"
                disabled={isAdding}
                aria-label="Care Level"
              >
                {Object.values(TreatmentCareLevel).map((level) => (
                  <option key={level} value={level}>
                    {toTitleCase(level.replace(/_/g, ' '))}
                  </option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel required>Facility</FormLabel>
              <FormSelect
                value={form.provider_type}
                onChange={(event) => {
                  update('provider_type', event.target.value as ProviderFormData['provider_type']);
                }}
                placeholder="Select"
                disabled={isAdding}
                aria-label="Facility"
              >
                <option value="clinic">Non-hospital</option>
                <option value="hospital">Hospital</option>
              </FormSelect>
            </FormField>
          </div>
        </FormGroup>

        {error && <FormError>{error}</FormError>}

        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className={
            'flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold ' +
            'bg-blue-600 text-white hover:bg-blue-700 ' +
            'disabled:cursor-not-allowed disabled:opacity-50 ' +
            'transition-colors duration-150'
          }
        >
          {isAdding ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            getAddButtonLabel(isEditing)
          )}
        </button>
      </div>

      <Dialog open={showSearchModal} onOpenChange={setShowSearchModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm">Search Provider Directory</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={modalSearch.query}
                onChange={(event) => modalSearch.setQuery(event.target.value)}
                placeholder="Search by provider name..."
                className="w-full rounded-lg border border-zinc-200 bg-white py-2 pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Search provider directory"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
                {modalSearch.isSearching && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />
                )}
                {!modalSearch.isSearching && modalSearch.query && (
                  <button
                    type="button"
                    onClick={() => modalSearch.clear()}
                    className="text-zinc-400 hover:text-zinc-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            </div>

            {!modalSearch.isSearching &&
              modalSearch.hasSearched &&
              modalSearch.resolveMatches.length === 0 &&
              modalSearch.googleResults.length === 0 && (
                <p className="py-6 text-center text-sm text-zinc-500">
                  No providers found for &ldquo;{modalSearch.query}&rdquo;
                </p>
              )}

            {(modalSearch.resolveMatches.length > 0 || modalSearch.googleResults.length > 0) && (
              <div className="max-h-72 overflow-y-auto rounded-lg border border-zinc-200">
                {modalSearch.resolveMatches.length > 0 && (
                  <>
                    <div className="sticky top-0 z-10 flex items-center gap-1.5 border-b border-zinc-200 bg-zinc-50 px-3 py-1.5">
                      <Building2 className="h-3 w-3 text-zinc-500" />
                      <span className="text-xs font-semibold text-zinc-600">
                        Directory ({modalSearch.resolveMatches.length})
                      </span>
                    </div>
                    {modalSearch.resolveMatches.slice(0, 8).map((match) => (
                      <button
                        key={match.provider_service_id}
                        type="button"
                        onClick={() => handlePickInternalFromModal(match)}
                        className="w-full border-b border-zinc-100 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-zinc-50"
                      >
                        <p className="text-sm font-medium text-zinc-900">{match.name}</p>
                        {(match.formatted_address || match.subtitle) && (
                          <p className="mt-0.5 flex items-center gap-1 text-xs text-zinc-500">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">
                              {match.formatted_address || match.subtitle}
                            </span>
                          </p>
                        )}
                        {match.phone && <p className="ml-4 mt-0.5 text-xs text-zinc-400">{match.phone}</p>}
                      </button>
                    ))}
                  </>
                )}

                {modalSearch.googleResults.length > 0 && (
                  <>
                    <div className="sticky top-0 z-10 flex items-center gap-1.5 border-b border-zinc-200 bg-zinc-50 px-3 py-1.5">
                      <Globe className="h-3 w-3 text-emerald-500" />
                      <span className="text-xs font-semibold text-zinc-600">
                        Google Places ({modalSearch.googleResults.length})
                      </span>
                    </div>
                    {modalSearch.googleResults.slice(0, 8).map((prediction) => (
                      <button
                        key={prediction.place_id}
                        type="button"
                        onClick={() => handlePickGoogleFromModal(prediction)}
                        disabled={isFetchingGoogleDetails}
                        className="w-full border-b border-zinc-100 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-zinc-50 disabled:opacity-50"
                      >
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 shrink-0 text-emerald-500" />
                          <p className="truncate text-sm font-medium text-zinc-900">
                            {prediction.main_text ?? prediction.description}
                          </p>
                        </div>
                        {prediction.secondary_text && (
                          <p className="ml-4 mt-0.5 truncate text-xs text-zinc-500">
                            {compactLocation(prediction.secondary_text)}
                          </p>
                        )}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
